/**
 * LOT 470 — Global accessibility helpers.
 *
 * Three pieces:
 *   - SkipToContent  : keyboard-only link "Skip to main content" at the very top
 *   - VisuallyHidden : utility for SR-only labels
 *   - useTrapFocus   : hook that traps Tab focus inside a modal/dialog
 *
 * Compliant with WCAG 2.1 AA basics. Reduces the a11y debt on icon-only
 * buttons (combined with title/aria-label LOT 436 F2).
 */
import React, { useEffect, useRef } from 'react';

export function SkipToContent({ targetId = 'main-content' }) {
  return (
    <a
      href={`#${targetId}`}
      className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[9999] focus:bg-charcoal focus:text-ivory focus:px-4 focus:py-2 focus:rounded-sm"
      data-testid="skip-to-content-link"
    >
      Aller au contenu principal
    </a>
  );
}

export function VisuallyHidden({ children }) {
  return <span className="sr-only">{children}</span>;
}

export function useTrapFocus(active = true) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;
    const container = containerRef.current;
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const previouslyFocused = document.activeElement;
    first.focus();

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      try { previouslyFocused?.focus?.(); } catch { /* ignore */ }
    };
  }, [active]);

  return containerRef;
}
